import 'leaflet/dist/leaflet.css';
import './styles.css';
import type * as L from 'leaflet';
import {
  demoSales,
  fetchCountyBoundary,
  fetchParcelAtPoint,
  fetchParcelBoundary,
  fetchSales,
  loadCache,
  saveCache,
} from './data';
import { filterRecords as filterSaleRecords } from './filtering';
import { addMonths, escapeHtml, formatCurrency, formatNumber, formatPricePerSqft, isoDate } from './format';
import {
  boundaryBounds,
  clearParcelBoundary,
  createSalesMap,
  openRenderedMarker,
  renderCountyBoundary,
  renderParcelBoundary,
  renderSaleMarkers,
  renderedMarkerForRecord,
} from './mapRendering';
import { fetchParcelDetails, formatBedroomBathSummary, formatYearBuilt, type ParcelDetails } from './parcelDetails';
import { fetchParcelHistory, type ParcelHistory } from './parcelHistory';
import { renderDistribution } from './priceDistribution';
import { setupParcelSearch } from './parcelSearch';
import type { FilterState, ParcelIdentifyResult, SaleRecord } from './types';

const { map, countyBoundaryLayer, salesLayer, parcelBoundaryLayer } = createSalesMap();
let allSales: SaleRecord[] = [];
let saleTypeOptions: string[] = [];
let selectedSaleTypes = new Set<string>();
let landClassOptions: string[] = [];
let selectedLandClasses = new Set<string>();
let sqftBounds: { min: number; max: number } | null = null;
let selectedSqftMin: number | null = null;
let selectedSqftMax: number | null = null;
let selectedSale: SaleRecord | null = null;
let selectedParcel: ParcelIdentifyResult | null = null;
let latestParcelSelectionId = 0;
let latestMapIdentifyRequestId = 0;
let latestSalesRenderRequestId = 0;

// The parcel panel has its own in-page navigation stack because the URL does not encode
// map extent or selected sale; browser history is only used to replay these snapshots.
type NavigationEntry = {
  center: [number, number];
  zoom: number;
  saleId: string | null;
  parcel: ParcelIdentifyResult | null;
};

type GisCharterHistoryState = {
  gisCharter: true;
  navigationIndex: number;
};

let navigationEntries: NavigationEntry[] = [];
let activeNavigationIndex = 0;
let isRestoringNavigation = false;
let hasInitializedBrowserHistory = false;

const filtersForm = document.querySelector<HTMLFormElement>('#filters')!;
const presetMonths = document.querySelector<HTMLSelectElement>('#presetMonths')!;
const presetOptions = Array.from(document.querySelectorAll<HTMLButtonElement>('.date-preset-option'));
const saleWindowToggle = document.querySelector<HTMLButtonElement>('#saleWindowToggle')!;
const customDateRange = document.querySelector<HTMLElement>('#customDateRange')!;
const dateFrom = document.querySelector<HTMLInputElement>('#dateFrom')!;
const dateTo = document.querySelector<HTMLInputElement>('#dateTo')!;
const minPrice = document.querySelector<HTMLInputElement>('#minPrice')!;
const maxPrice = document.querySelector<HTMLInputElement>('#maxPrice')!;
const priceToggle = document.querySelector<HTMLButtonElement>('#priceToggle')!;
const sqftRangeControl = document.querySelector<HTMLElement>('#sqftRangeControl')!;
const sqftMin = document.querySelector<HTMLInputElement>('#sqftMin')!;
const sqftMax = document.querySelector<HTMLInputElement>('#sqftMax')!;
const sqftRangeLabel = document.querySelector<HTMLOutputElement>('#sqftRangeLabel')!;
const acreageMin = document.querySelector<HTMLInputElement>('#acreageMin')!;
const acreageMax = document.querySelector<HTMLInputElement>('#acreageMax')!;
const saleTypeOptionsContainer = document.querySelector<HTMLElement>('#saleTypeOptions')!;
const landClassOptionsContainer = document.querySelector<HTMLElement>('#landClassOptions')!;
const resetFilters = document.querySelector<HTMLButtonElement>('#resetFilters')!;
const visibleSalesCount = document.querySelector<HTMLElement>('#visibleSalesCount')!;
const statusText = document.querySelector<HTMLElement>('#statusText')!;
const distributionSummary = document.querySelector<HTMLElement>('#distributionSummary')!;
const distributionChart = document.querySelector<HTMLElement>('#distributionChart')!;
const toast = document.querySelector<HTMLElement>('#toast')!;
const mapShell = document.querySelector<HTMLElement>('.map-shell')!;
const mapLoading = document.querySelector<HTMLElement>('#mapLoading')!;
const mapLoadingText = document.querySelector<HTMLElement>('#mapLoadingText')!;
const mapInstruction = document.querySelector<HTMLElement>('.map-instruction')!;
const legendToggle = document.querySelector<HTMLButtonElement>('#legendToggle')!;
const legendPanel = document.querySelector<HTMLElement>('#legendPanel')!;
const legendClose = document.querySelector<HTMLButtonElement>('#legendClose')!;
const parcelPanel = document.querySelector<HTMLElement>('#parcelPanel')!;
const parcelPanelTitle = document.querySelector<HTMLElement>('#parcelPanelTitle')!;
const parcelPanelKind = document.querySelector<HTMLElement>('#parcelPanelKind')!;
const parcelPanelBody = document.querySelector<HTMLElement>('#parcelPanelBody')!;
const parcelPanelClose = document.querySelector<HTMLButtonElement>('#parcelPanelClose')!;
const parcelBack = document.querySelector<HTMLButtonElement>('#parcelBack')!;
const dropdowns = [
  {
    toggle: document.querySelector<HTMLButtonElement>('#saleWindowToggle')!,
    panel: document.querySelector<HTMLElement>('#saleWindowMenu')!,
  },
  {
    toggle: document.querySelector<HTMLButtonElement>('#priceToggle')!,
    panel: document.querySelector<HTMLElement>('#priceMenu')!,
  },
  {
    toggle: document.querySelector<HTMLButtonElement>('#moreFiltersToggle')!,
    panel: document.querySelector<HTMLElement>('#moreFiltersMenu')!,
  },
];

const LIVE_FILTER_DELAY_MS = 250;
const LIVE_REFRESH_DELAY_MS = 350;
const PARCEL_PHOTO_TIMEOUT_MS = 8000;
const MAP_INSTRUCTION_STORAGE_KEY = 'gischarter-map-instruction-complete-v1';
const CURRENCY_INPUT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const NARROW_PARCEL_VIEW_QUERY = '(max-width: 980px)';
let liveFilterTimer: number | null = null;
let liveRefreshTimer: number | null = null;
let toastTimer: number | null = null;
let mapLoadingStartedAt = 0;
let mapLoadingHideTimer: number | null = null;
let pendingSalesRenderFrame: number | null = null;
let sqftDragState:
  | {
      pointerId: number;
      input: HTMLInputElement;
    }
  | null = null;

const parcelSearch = setupParcelSearch({
  onOpen() {
    // A map click started before search opened must not replace this interaction.
    latestMapIdentifyRequestId += 1;
    closeDropdowns();
    setLegendOpen(false);
  },
  onSelect(parcel) {
    void selectIdentifiedParcel(parcel);
    parcelPanelTitle.tabIndex = -1;
    parcelPanelTitle.focus({ preventScroll: true });
  },
});

type GisCharterTestApi = {
  openDemoPopup: (index: number) => boolean;
  highlightedBoundaryCount: () => number;
  countyBoundaryCount: () => number;
  mapView: () => {
    center: [number, number];
    zoom: number;
    selectedSaleId: string | null;
    selectedParcelNumber: string | null;
    historyIndex: number;
  };
};

function registerTestHooks(): void {
  if (!new URLSearchParams(window.location.search).has('demo')) return;

  (window as Window & { __gisCharterTest__?: GisCharterTestApi }).__gisCharterTest__ = {
    openDemoPopup(index: number) {
      return openRenderedMarker(index);
    },
    highlightedBoundaryCount() {
      return parcelBoundaryLayer.getLayers().length;
    },
    countyBoundaryCount() {
      return countyBoundaryLayer.getLayers().length;
    },
    mapView() {
      const center = map.getCenter();
      return {
        center: [center.lat, center.lng],
        zoom: map.getZoom(),
        selectedSaleId: selectedSale?.id ?? null,
        selectedParcelNumber: selectedParcel?.parcelNumber ?? null,
        historyIndex: activeNavigationIndex,
      };
    },
  };
}

function setDefaultDates(months = 12): void {
  const today = new Date();
  dateTo.value = isoDate(today);
  dateFrom.value = isoDate(addMonths(today, -months));
  presetMonths.value = String(months);
  syncCustomDateControls();
}

function syncCustomDateControls(): void {
  customDateRange.hidden = presetMonths.value !== 'custom';
  for (const option of presetOptions) {
    option.setAttribute('aria-pressed', String(option.dataset.presetMonths === presetMonths.value));
  }
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function rangeFromValues(values: number[]): { min: number; max: number } | null {
  const validValues = values.filter((value) => Number.isFinite(value));
  if (validValues.length === 0) return null;
  return {
    min: Math.floor(Math.min(...validValues) / 50) * 50,
    max: Math.ceil(Math.max(...validValues) / 50) * 50,
  };
}

function parseCurrencyInput(value: string): number | null {
  const cleaned = value.replace(/[$,\s]/g, '').trim();
  if (cleaned === '') return null;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? Math.round(parsed) : null;
}

function formatCurrencyInputValue(value: number | null): string {
  return value === null ? '' : CURRENCY_INPUT_FORMATTER.format(value);
}

function formatCurrencyInput(input: HTMLInputElement): void {
  const parsed = parseCurrencyInput(input.value);
  input.value = formatCurrencyInputValue(parsed);
}

function renderPriceToggleLabel(filters = getFilters()): void {
  if (filters.minPrice === null && filters.maxPrice === null) {
    priceToggle.textContent = 'Price';
    return;
  }

  if (filters.minPrice !== null && filters.maxPrice !== null) {
    priceToggle.textContent = `Price ${formatCurrency(filters.minPrice)} to ${formatCurrency(filters.maxPrice)}`;
    return;
  }

  if (filters.minPrice !== null) {
    priceToggle.textContent = `Price ${formatCurrency(filters.minPrice)}+`;
    return;
  }

  priceToggle.textContent = `Price up to ${formatCurrency(filters.maxPrice)}`;
}

function renderSaleWindowToggleLabel(filters = getFilters()): void {
  if (presetMonths.value === 'custom') {
    saleWindowToggle.textContent = `Sale window ${filters.dateFrom || 'Any'} to ${filters.dateTo || 'Any'}`;
    return;
  }

  const selectedPreset = presetMonths.selectedOptions[0]?.textContent?.trim();
  saleWindowToggle.textContent = selectedPreset ? `Sale window ${selectedPreset}` : 'Sale window';
}

function isFullSqftRange(): boolean {
  return (
    sqftBounds === null ||
    selectedSqftMin === null ||
    selectedSqftMax === null ||
    (selectedSqftMin <= sqftBounds.min && selectedSqftMax >= sqftBounds.max)
  );
}

function updateSqftLabel(): void {
  if (!sqftBounds || selectedSqftMin === null || selectedSqftMax === null) {
    sqftRangeLabel.textContent = 'No sq ft data';
    return;
  }

  if (isFullSqftRange()) {
    sqftRangeLabel.textContent = `${formatNumber(sqftBounds.min)}-${formatNumber(sqftBounds.max)} sq ft`;
  } else {
    sqftRangeLabel.textContent = `${formatNumber(selectedSqftMin)}-${formatNumber(selectedSqftMax)} sq ft`;
  }
}

function updateSqftRangeTrack(): void {
  if (!sqftBounds || selectedSqftMin === null || selectedSqftMax === null) {
    sqftRangeControl.classList.add('is-disabled');
    sqftRangeControl.classList.remove('is-dragging-min', 'is-dragging-max');
    sqftRangeControl.style.setProperty('--sqft-start', '0%');
    sqftRangeControl.style.setProperty('--sqft-end', '0%');
    return;
  }

  const span = Math.max(sqftBounds.max - sqftBounds.min, 1);
  const minPct = ((selectedSqftMin - sqftBounds.min) / span) * 100;
  const maxPct = ((selectedSqftMax - sqftBounds.min) / span) * 100;
  const start = Math.max(0, Math.min(100, Math.min(minPct, maxPct)));
  const end = Math.max(0, Math.min(100, Math.max(minPct, maxPct)));

  sqftRangeControl.classList.remove('is-disabled');
  sqftRangeControl.style.setProperty('--sqft-start', `${start}%`);
  sqftRangeControl.style.setProperty('--sqft-end', `${end}%`);
}

function valueFromSqftPointer(clientX: number): number | null {
  if (!sqftBounds) return null;

  const rect = sqftRangeControl.getBoundingClientRect();
  if (rect.width <= 0) return null;

  const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  const rawValue = sqftBounds.min + (sqftBounds.max - sqftBounds.min) * ratio;
  const step = Number(sqftMin.step) || 50;
  const steppedValue = sqftBounds.min + Math.round((rawValue - sqftBounds.min) / step) * step;

  return Math.min(sqftBounds.max, Math.max(sqftBounds.min, steppedValue));
}

function closestSqftInput(value: number): HTMLInputElement {
  const minValue = Number(sqftMin.value);
  const maxValue = Number(sqftMax.value);
  const minDistance = Math.abs(value - minValue);
  const maxDistance = Math.abs(value - maxValue);

  if (minDistance === maxDistance) {
    return value < (minValue + maxValue) / 2 ? sqftMin : sqftMax;
  }

  return minDistance < maxDistance ? sqftMin : sqftMax;
}

function setActiveSqftInput(input: HTMLInputElement | null): void {
  sqftRangeControl.classList.toggle('is-dragging-min', input === sqftMin);
  sqftRangeControl.classList.toggle('is-dragging-max', input === sqftMax);
}

function setSqftInputValue(input: HTMLInputElement, value: number): void {
  input.value = String(value);
  handleSqftInput(input);
}

function renderSqftRange(records = allSales, preserveSelection = true): void {
  const nextBounds = rangeFromValues(records.map((record) => record.buildingSize).filter((value): value is number => value !== null));
  const previousBounds = sqftBounds;
  const previousWasFull = isFullSqftRange();
  const previousMin = selectedSqftMin;
  const previousMax = selectedSqftMax;

  sqftBounds = nextBounds;

  if (!sqftBounds) {
    selectedSqftMin = null;
    selectedSqftMax = null;
    sqftMin.disabled = true;
    sqftMax.disabled = true;
    sqftMin.value = '0';
    sqftMax.value = '0';
    updateSqftLabel();
    updateSqftRangeTrack();
    return;
  }

  // Preserve a user-narrowed sqft range across data refreshes, but reset to the full range
  // when the previous selection represented "all" for the old result set.
  if (!preserveSelection || !previousBounds || previousWasFull || previousMin === null || previousMax === null) {
    selectedSqftMin = sqftBounds.min;
    selectedSqftMax = sqftBounds.max;
  } else {
    selectedSqftMin = Math.min(Math.max(previousMin, sqftBounds.min), sqftBounds.max);
    selectedSqftMax = Math.min(Math.max(previousMax, sqftBounds.min), sqftBounds.max);
    if (selectedSqftMin > selectedSqftMax) selectedSqftMin = selectedSqftMax;
  }

  for (const input of [sqftMin, sqftMax]) {
    input.disabled = false;
    input.min = String(sqftBounds.min);
    input.max = String(sqftBounds.max);
    input.step = '50';
  }

  sqftMin.value = String(selectedSqftMin);
  sqftMax.value = String(selectedSqftMax);
  updateSqftLabel();
  updateSqftRangeTrack();
}

function optionsFromInputs(container: HTMLElement, name: string): string[] {
  return Array.from(container.querySelectorAll<HTMLInputElement>(`input[name="${name}"]:checked`)).map((input) => input.value);
}

function syncSelectionFromInputs(): void {
  selectedSaleTypes = new Set(optionsFromInputs(saleTypeOptionsContainer, 'saleType'));
  selectedLandClasses = new Set(optionsFromInputs(landClassOptionsContainer, 'landClass'));
}

function renderOptionSet(
  container: HTMLElement,
  options: string[],
  selectedOptions: Set<string>,
  name: string,
  idPrefix: string,
  emptyMessage: string,
): void {
  if (options.length === 0) {
    container.innerHTML = `<p class="empty-sale-types">${emptyMessage}</p>`;
    return;
  }

  container.innerHTML = options
    .map(
      (option, index) => `
        <label class="choice-option" for="${idPrefix}${index}">
          <input
            id="${idPrefix}${index}"
            name="${name}"
            type="checkbox"
            value="${escapeHtml(option)}"
            ${selectedOptions.has(option) ? 'checked' : ''}
          />
          <span>${escapeHtml(option)}</span>
        </label>
      `,
    )
    .join('');
}

function nextSelection(options: string[], previousOptions: string[], previousSelection: Set<string>, preserveSelection: boolean): Set<string> {
  const previousWasAllSelected =
    previousOptions.length > 0 && previousOptions.every((option) => previousSelection.has(option));

  if (!preserveSelection || previousOptions.length === 0 || previousWasAllSelected) {
    return new Set(options);
  }

  return new Set(options.filter((option) => previousSelection.has(option)));
}

function renderDynamicFilters(records = allSales, preserveSelection = true): void {
  const previousSaleTypeOptions = saleTypeOptions;
  const previousLandClassOptions = landClassOptions;
  const previousSaleTypes = selectedSaleTypes;
  const previousLandClasses = selectedLandClasses;

  saleTypeOptions = uniqueSorted(records.map((record) => record.saleType));
  landClassOptions = uniqueSorted(records.map((record) => record.landClass));

  selectedSaleTypes = nextSelection(saleTypeOptions, previousSaleTypeOptions, previousSaleTypes, preserveSelection);
  selectedLandClasses = nextSelection(landClassOptions, previousLandClassOptions, previousLandClasses, preserveSelection);

  renderSqftRange(records, preserveSelection);
  renderOptionSet(saleTypeOptionsContainer, saleTypeOptions, selectedSaleTypes, 'saleType', 'saleType', 'Sale types load with records.');
  renderOptionSet(
    landClassOptionsContainer,
    landClassOptions,
    selectedLandClasses,
    'landClass',
    'landClass',
    'Land classes load with records.',
  );
}

function getFilters(): FilterState {
  return {
    presetMonths: presetMonths.value === 'custom' ? 0 : Number(presetMonths.value),
    dateFrom: dateFrom.value,
    dateTo: dateTo.value,
    minPrice: parseCurrencyInput(minPrice.value),
    maxPrice: parseCurrencyInput(maxPrice.value),
    sqftMin: sqftBounds ? Number(sqftMin.value) : null,
    sqftMax: sqftBounds ? Number(sqftMax.value) : null,
    saleTypes: optionsFromInputs(saleTypeOptionsContainer, 'saleType'),
    landClasses: optionsFromInputs(landClassOptionsContainer, 'landClass'),
    acreageMin: acreageMin.value ? Number(acreageMin.value) : null,
    acreageMax: acreageMax.value ? Number(acreageMax.value) : null,
  };
}

function getFilteredSales(): SaleRecord[] {
  return filterSaleRecords(allSales, getFilters(), {
    sqftBounds,
    isFullSqftRange,
    saleTypeOptions,
    landClassOptions,
  });
}

function renderActiveFilters(): void {
  const filters = getFilters();

  renderSaleWindowToggleLabel(filters);
  renderPriceToggleLabel(filters);
}

function resetAllFilters(): void {
  minPrice.value = '';
  maxPrice.value = '';
  acreageMin.value = '';
  acreageMax.value = '';
  setDefaultDates(12);
  renderDynamicFilters(allSales, false);
  refreshData();
}

function setDropdownOpen(toggle: HTMLButtonElement, panel: HTMLElement, isOpen: boolean): void {
  toggle.setAttribute('aria-expanded', String(isOpen));
  panel.hidden = !isOpen;
}

function clearDropdownPosition(panel: HTMLElement): void {
  panel.style.removeProperty('position');
  panel.style.removeProperty('left');
  panel.style.removeProperty('top');
  panel.style.removeProperty('right');
  panel.style.removeProperty('width');
  panel.style.removeProperty('max-width');
  panel.style.removeProperty('z-index');
}

function positionSaleWindowDropdown(toggle: HTMLButtonElement, panel: HTMLElement): void {
  if (!window.matchMedia('(max-width: 980px)').matches) {
    clearDropdownPosition(panel);
    return;
  }

  // On mobile the top filter bar scrolls horizontally, so fixed coordinates keep the
  // sale-window menu anchored to the visible toggle instead of the scrolled nav content.
  const toggleRect = toggle.getBoundingClientRect();
  const panelWidth = Math.min(400, window.innerWidth - 36);
  const panelLeft = Math.max(12, Math.min(toggleRect.left, window.innerWidth - panelWidth - 12));
  const searchBottom = document.querySelector<HTMLElement>('#parcelSearchToggle')!.getBoundingClientRect().bottom;

  panel.style.position = 'fixed';
  panel.style.left = `${panelLeft}px`;
  panel.style.top = `${Math.round(Math.max(toggleRect.bottom, searchBottom) + 8)}px`;
  panel.style.right = 'auto';
  panel.style.width = `${panelWidth}px`;
  panel.style.maxWidth = `${panelWidth}px`;
  panel.style.zIndex = '850';
}

function closeDropdowns(except?: HTMLElement): void {
  for (const dropdown of dropdowns) {
    if (dropdown.panel === except) continue;
    clearDropdownPosition(dropdown.panel);
    setDropdownOpen(dropdown.toggle, dropdown.panel, false);
  }
}

function toggleDropdown(toggle: HTMLButtonElement, panel: HTMLElement): void {
  const shouldOpen = panel.hidden === true;
  closeDropdowns(panel);
  if (toggle.id === 'saleWindowToggle' && shouldOpen) {
    positionSaleWindowDropdown(toggle, panel);
  } else {
    clearDropdownPosition(panel);
  }
  setDropdownOpen(toggle, panel, shouldOpen);
}

function setLegendOpen(isOpen: boolean): void {
  legendPanel.hidden = !isOpen;
  legendToggle.setAttribute('aria-expanded', String(isOpen));
  const label = isOpen ? 'Close map info' : 'Open map info';
  legendToggle.setAttribute('aria-label', label);
  legendToggle.title = label;
}

function renderMissingParcelPhoto(photoElement: HTMLElement): void {
  photoElement.className = 'parcel-photo is-empty';
  photoElement.innerHTML = '<span class="parcel-photo-empty-icon" aria-hidden="true"></span><span>No parcel photo available</span>';
}

function renderParcelPhoto(photoElement: HTMLElement, details: ParcelDetails | null, address: string): void {
  if (!details?.photoUrl) {
    renderMissingParcelPhoto(photoElement);
    return;
  }

  const image = document.createElement('img');
  image.width = 800;
  image.height = 600;
  image.decoding = 'async';
  let settled = false;
  // Parcel images are proxied from NCPTS and can fail independently from details, so the
  // panel falls back instead of leaving a broken or indefinitely loading photo slot.
  const timeoutId = window.setTimeout(() => {
    if (settled) return;
    settled = true;
    renderMissingParcelPhoto(photoElement);
  }, PARCEL_PHOTO_TIMEOUT_MS);

  image.addEventListener('load', () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeoutId);
    photoElement.className = 'parcel-photo is-loaded';
    photoElement.replaceChildren(image);
  });

  image.addEventListener('error', () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeoutId);
    renderMissingParcelPhoto(photoElement);
  });

  image.alt = `Parcel photo for ${address}`;
  image.src = details.photoUrl;
}

function isLandOnlySale(record: SaleRecord): boolean {
  return record.saleType.trim().toUpperCase() === 'LAND ONLY';
}

function formatBookPage(book: string, page: string): string {
  if (book && page) return `${book} / ${page}`;
  return book || page || 'N/A';
}

function renderHistoryAmount(value: number | null): string {
  return value !== null && value > 0 ? formatCurrency(value) : 'N/A';
}

function renderParcelHistorySections(history: ParcelHistory | null, isLoading: boolean, excludedSaleId: string | null): string {
  if (isLoading) {
    return `
      <section class="parcel-history-section" aria-label="Past parcel sales">
        <h3>Past sales</h3>
        <p class="parcel-history-empty">Past sales loading…</p>
      </section>
      <section class="parcel-history-section" aria-label="Recorded deed history">
        <h3>Deed history</h3>
        <p class="parcel-history-empty">Deed history loading…</p>
      </section>
    `;
  }

  const sales = (history?.sales ?? []).filter((sale) => sale.id !== excludedSaleId);
  const saleRows =
    sales.length > 0
      ? `<ol class="parcel-history-list">${sales
          .map(
            (sale) => `
              <li>
                <span class="parcel-history-main">${escapeHtml(sale.saleDateLabel)} - ${escapeHtml(renderHistoryAmount(sale.price))}</span>
                <span>${escapeHtml(sale.saleType)} - Deed ${escapeHtml(formatBookPage(sale.deedBook, sale.deedPage))}</span>
              </li>
            `,
          )
          .join('')}</ol>`
      : '<p class="parcel-history-empty">No other ArcGIS sales found for this parcel.</p>';

  const deeds = history?.deeds ?? [];
  const deedRows =
    deeds.length > 0
      ? `<ol class="parcel-history-list">${deeds
          .map(
            (deed) => `
              <li>
                <span class="parcel-history-main">${escapeHtml(deed.deedDateLabel)} - ${escapeHtml(renderHistoryAmount(deed.salePrice))}</span>
                <span>${escapeHtml(deed.deedType)} - Book/Page ${escapeHtml(formatBookPage(deed.book, deed.page))}</span>
              </li>
            `,
          )
          .join('')}</ol>`
      : '<p class="parcel-history-empty">No NCPTS deed history found for this parcel.</p>';

  return `
    <section class="parcel-history-section" aria-label="Past parcel sales">
      <h3>Past sales</h3>
      ${saleRows}
    </section>
    <section class="parcel-history-section" aria-label="Recorded deed history">
      <h3>Deed history</h3>
      ${deedRows}
    </section>
  `;
}

function renderParcelHistory(record: SaleRecord, history: ParcelHistory | null, isLoading: boolean): string {
  return renderParcelHistorySections(history, isLoading, record.id);
}

function renderParcelPanel(
  record: SaleRecord,
  details: ParcelDetails | null,
  history: ParcelHistory | null,
  isLoading: boolean,
): void {
  parcelPanelKind.textContent = 'Selected sale';
  const propertyLink = record.propertyUrl
    ? `<a href="${record.propertyUrl}" target="_blank" rel="noreferrer">Open property summary</a>`
    : '<span>No property summary link available</span>';
  const showBuildingDetails = !isLandOnlySale(record);
  const bedroomBathLabel = isLoading ? 'Beds/Baths loading…' : formatBedroomBathSummary(details);
  const yearBuiltLabel = isLoading ? 'Year built loading…' : formatYearBuilt(details);
  const buildingDetails = showBuildingDetails
    ? `
      <div><dt>Beds/Baths</dt><dd class="parcel-bedroom-bath">${escapeHtml(bedroomBathLabel)}</dd></div>
      <div><dt>Year built</dt><dd class="parcel-year-built">${escapeHtml(yearBuiltLabel)}</dd></div>
      <div><dt>Building size</dt><dd>${formatNumber(record.buildingSize)} sq ft</dd></div>
      <div><dt>Price per sq ft</dt><dd>${formatPricePerSqft(record.pricePerSqft)}</dd></div>
    `
    : '';
  const parcelPhoto = isLoading
    ? '<div class="parcel-photo is-loading"><span class="parcel-photo-spinner" aria-hidden="true"></span><span>Photo loading…</span></div>'
    : '<div class="parcel-photo is-empty"><span class="parcel-photo-empty-icon" aria-hidden="true"></span><span>No parcel photo available</span></div>';

  parcelPanelTitle.textContent = record.address;
  parcelPanel.hidden = false;
  updateNavigationControls();
  parcelPanelBody.innerHTML = `
    ${parcelPhoto}
    <dl class="parcel-fact-grid">
      <div><dt>Sale price</dt><dd>${formatCurrency(record.price)}</dd></div>
      <div><dt>Sale date</dt><dd>${escapeHtml(record.saleDateLabel)}</dd></div>
      <div><dt>Parcel</dt><dd>${escapeHtml(record.parcelNumber || 'N/A')}</dd></div>
      ${buildingDetails}
      <div><dt>Acreage</dt><dd>${formatNumber(record.acreage)}</dd></div>
      <div><dt>Sale type</dt><dd>${escapeHtml(record.saleType)}</dd></div>
    </dl>
    ${renderParcelHistory(record, history, isLoading)}
    ${propertyLink}
  `;

  if (!isLoading) {
    const photoElement = parcelPanelBody.querySelector<HTMLElement>('.parcel-photo');
    if (photoElement) renderParcelPhoto(photoElement, details, record.address);
  }
}

function renderIdentifiedParcelPanel(
  parcel: ParcelIdentifyResult,
  details: ParcelDetails | null,
  history: ParcelHistory | null,
  isLoading: boolean,
): void {
  parcelPanelKind.textContent = 'Selected property';
  const propertyLink = parcel.propertyUrl
    ? `<a href="${parcel.propertyUrl}" target="_blank" rel="noreferrer">Open property summary</a>`
    : '<span>No property summary link available</span>';
  const hasBuilding = parcel.buildingSize !== null && parcel.buildingSize > 0;
  const bedroomBathLabel = isLoading ? 'Beds/Baths loading…' : formatBedroomBathSummary(details);
  const yearBuiltLabel = isLoading ? 'Year built loading…' : formatYearBuilt(details);
  const buildingDetails = hasBuilding
    ? `
      <div><dt>Beds/Baths</dt><dd class="parcel-bedroom-bath">${escapeHtml(bedroomBathLabel)}</dd></div>
      <div><dt>Year built</dt><dd class="parcel-year-built">${escapeHtml(yearBuiltLabel)}</dd></div>
      <div><dt>Heated area</dt><dd>${formatNumber(parcel.buildingSize)} sq ft</dd></div>
    `
    : '';
  const deedLabel = formatBookPage(parcel.deedBook, parcel.deedPage);
  const parcelPhoto = isLoading
    ? '<div class="parcel-photo is-loading"><span class="parcel-photo-spinner" aria-hidden="true"></span><span>Photo loading…</span></div>'
    : '<div class="parcel-photo is-empty"><span class="parcel-photo-empty-icon" aria-hidden="true"></span><span>No parcel photo available</span></div>';

  parcelPanelTitle.textContent = parcel.address;
  parcelPanel.hidden = false;
  updateNavigationControls();
  parcelPanelBody.innerHTML = `
    ${parcelPhoto}
    <dl class="parcel-fact-grid">
      <div><dt>Parcel</dt><dd>${escapeHtml(parcel.parcelNumber || 'N/A')}</dd></div>
      ${parcel.owner ? `<div class="parcel-owner"><dt>Recorded owner</dt><dd>${escapeHtml(parcel.owner)}</dd></div>` : ''}
      <div><dt>City</dt><dd>${escapeHtml(parcel.city || 'N/A')}</dd></div>
      <div><dt>Land class</dt><dd>${escapeHtml(parcel.landClass || 'N/A')}</dd></div>
      <div><dt>Acreage</dt><dd>${formatNumber(parcel.acreage)}</dd></div>
      ${buildingDetails}
      <div><dt>Assessed value</dt><dd>${formatCurrency(parcel.assessedValue)}</dd></div>
      <div><dt>Last sale</dt><dd>${formatCurrency(parcel.lastSalePrice)}</dd></div>
      <div><dt>Last sale date</dt><dd>${escapeHtml(parcel.lastSaleDateLabel || 'N/A')}</dd></div>
      <div><dt>Deed</dt><dd>${escapeHtml(deedLabel)}</dd></div>
    </dl>
    ${renderParcelHistorySections(history, isLoading, null)}
    ${propertyLink}
  `;

  if (!isLoading) {
    const photoElement = parcelPanelBody.querySelector<HTMLElement>('.parcel-photo');
    if (photoElement) renderParcelPhoto(photoElement, details, parcel.address);
  }
}

function closeParcelPanel(clearBoundary = false): void {
  parcelSearch.cancelSelection();
  latestMapIdentifyRequestId += 1;
  selectedSale = null;
  selectedParcel = null;
  // Invalidate pending parcel boundary/details work so late async responses cannot reopen
  // or repaint a panel after the user has closed it or selected another marker.
  latestParcelSelectionId += 1;
  parcelPanel.hidden = true;
  if (clearBoundary) clearParcelBoundary(parcelBoundaryLayer);
  updateNavigationControls();
}

function currentNavigationSelection(): Pick<NavigationEntry, 'saleId' | 'parcel'> {
  if (selectedSale) return { saleId: selectedSale.id, parcel: null };
  if (selectedParcel) return { saleId: null, parcel: selectedParcel };
  return { saleId: null, parcel: null };
}

function snapshotNavigationEntry(selection: Pick<NavigationEntry, 'saleId' | 'parcel'> = currentNavigationSelection()): NavigationEntry {
  const center = map.getCenter();
  return {
    center: [center.lat, center.lng],
    zoom: map.getZoom(),
    saleId: selection.saleId,
    parcel: selection.parcel,
  };
}

function browserHistoryState(index: number): GisCharterHistoryState {
  return { gisCharter: true, navigationIndex: index };
}

function isGisCharterHistoryState(state: unknown): state is GisCharterHistoryState {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as { gisCharter?: unknown }).gisCharter === true &&
    Number.isInteger((state as { navigationIndex?: unknown }).navigationIndex)
  );
}

function updateNavigationControls(): void {
  parcelBack.hidden = activeNavigationIndex <= 0;
}

function resetNavigationHistory(): void {
  navigationEntries = [snapshotNavigationEntry({ saleId: null, parcel: null })];
  activeNavigationIndex = 0;
  updateNavigationControls();

  if (hasInitializedBrowserHistory) {
    window.history.replaceState(browserHistoryState(0), '', window.location.href);
    return;
  }

  window.history.replaceState(browserHistoryState(0), '', window.location.href);
  hasInitializedBrowserHistory = true;
}

function pushNavigationEntry(entry: NavigationEntry): void {
  navigationEntries = navigationEntries.slice(0, activeNavigationIndex + 1);
  navigationEntries.push(entry);
  activeNavigationIndex = navigationEntries.length - 1;
  updateNavigationControls();
  window.history.pushState(browserHistoryState(activeNavigationIndex), '', window.location.href);
}

function findSaleById(saleId: string): SaleRecord | null {
  return allSales.find((record) => record.id === saleId) ?? null;
}

async function restoreNavigationEntry(entry: NavigationEntry): Promise<void> {
  isRestoringNavigation = true;

  if (entry.saleId) {
    const record = findSaleById(entry.saleId);
    const marker = record ? renderedMarkerForRecord(record.id) : null;

    if (record && marker) {
      await selectSale(record, marker, { updateHistory: false });
    } else {
      closeParcelPanel(true);
      map.flyTo(entry.center, entry.zoom, { duration: 0.55 });
    }
  } else if (entry.parcel) {
    await selectIdentifiedParcel(entry.parcel, { updateHistory: false });
  } else {
    closeParcelPanel(true);
    map.flyTo(entry.center, entry.zoom, { duration: 0.55 });
  }

  isRestoringNavigation = false;
  updateNavigationControls();
}

function restorePreviousMapView(): void {
  if (activeNavigationIndex <= 0) return;
  window.history.back();
}

function initializeMapInstruction(): void {
  try {
    mapInstruction.hidden = window.localStorage.getItem(MAP_INSTRUCTION_STORAGE_KEY) === 'true';
  } catch {
    mapInstruction.hidden = false;
  }
}

function dismissMapInstruction(): void {
  mapInstruction.hidden = true;
  try {
    window.localStorage.setItem(MAP_INSTRUCTION_STORAGE_KEY, 'true');
  } catch {
    // The hint still dismisses for the current page when storage is unavailable.
  }
}

function resetParcelPanelScroll(selectionId: number): void {
  const reset = () => {
    if (selectionId !== latestParcelSelectionId) return;
    parcelPanelBody.scrollTop = 0;
  };

  reset();
  window.requestAnimationFrame(reset);
}

function focusParcelBoundary(boundary: Parameters<typeof boundaryBounds>[0]): void {
  if (!window.matchMedia(NARROW_PARCEL_VIEW_QUERY).matches) {
    map.flyToBounds(boundaryBounds(boundary), {
      maxZoom: 18,
      paddingTopLeft: [36, 112],
      paddingBottomRight: [420, 72],
      duration: 0.55,
    });
    return;
  }

  const mapRect = mapShell.getBoundingClientRect();
  const topbarRect = document.querySelector<HTMLElement>('.topbar')?.getBoundingClientRect();
  const horizontalPadding = 16;
  const topPadding = Math.max(horizontalPadding, (topbarRect?.bottom ?? mapRect.top) - mapRect.top + 12);

  map.flyToBounds(boundaryBounds(boundary), {
    maxZoom: 16,
    paddingTopLeft: [horizontalPadding, topPadding],
    paddingBottomRight: [horizontalPadding, horizontalPadding],
    duration: 0.55,
  });
}

async function selectSale(
  record: SaleRecord,
  marker: L.CircleMarker,
  options: { updateHistory?: boolean } = {},
): Promise<void> {
  parcelSearch.close();
  latestMapIdentifyRequestId += 1;
  // Each selection gets a token because boundary and detail requests resolve separately and
  // may complete after the user has already moved to another parcel.
  const selectionId = ++latestParcelSelectionId;
  dismissMapInstruction();
  selectedSale = record;
  selectedParcel = null;
  if (options.updateHistory !== false && !isRestoringNavigation) {
    pushNavigationEntry(snapshotNavigationEntry({ saleId: record.id, parcel: null }));
  }
  closeDropdowns();
  setLegendOpen(false);
  renderParcelPanel(record, null, null, Boolean(record.parcelNumber));
  resetParcelPanelScroll(selectionId);

  const fallbackZoom = Math.max(map.getZoom(), 17);
  const fallbackToMarker = () => {
    map.flyTo(marker.getLatLng(), fallbackZoom, { duration: 0.55 });
  };

  if (record.parcelNumber) {
    const boundary = await fetchParcelBoundary(record.parcelNumber);
    if (selectionId !== latestParcelSelectionId || selectedSale !== record) return;

    if (boundary) {
      renderParcelBoundary(parcelBoundaryLayer, boundary);
      focusParcelBoundary(boundary);
    } else {
      clearParcelBoundary(parcelBoundaryLayer);
      fallbackToMarker();
    }

    const [details, history] = await Promise.all([
      fetchParcelDetails(record.parcelNumber),
      fetchParcelHistory(record.parcelNumber),
    ]);
    if (selectionId !== latestParcelSelectionId || selectedSale !== record) return;
    renderParcelPanel(record, details, history, false);
    return;
  }

  clearParcelBoundary(parcelBoundaryLayer);
  fallbackToMarker();
  renderParcelPanel(record, null, null, false);
}

async function selectIdentifiedParcel(
  parcel: ParcelIdentifyResult,
  options: { updateHistory?: boolean } = {},
): Promise<void> {
  parcelSearch.close();
  latestMapIdentifyRequestId += 1;
  const selectionId = ++latestParcelSelectionId;
  dismissMapInstruction();
  selectedSale = null;
  selectedParcel = parcel;
  if (options.updateHistory !== false && !isRestoringNavigation) {
    pushNavigationEntry(snapshotNavigationEntry({ saleId: null, parcel }));
  }
  closeDropdowns();
  setLegendOpen(false);
  renderIdentifiedParcelPanel(parcel, null, null, Boolean(parcel.parcelNumber));
  resetParcelPanelScroll(selectionId);
  renderParcelBoundary(parcelBoundaryLayer, parcel.boundary);
  focusParcelBoundary(parcel.boundary);

  if (!parcel.parcelNumber) {
    renderIdentifiedParcelPanel(parcel, null, null, false);
    return;
  }

  const [details, history] = await Promise.all([
    fetchParcelDetails(parcel.parcelNumber),
    fetchParcelHistory(parcel.parcelNumber),
  ]);
  if (selectionId !== latestParcelSelectionId || selectedParcel !== parcel) return;
  renderIdentifiedParcelPanel(parcel, details, history, false);
}

async function identifyParcelAtPoint(latlng: L.LatLng): Promise<void> {
  parcelSearch.close();
  const requestId = ++latestMapIdentifyRequestId;
  closeDropdowns();
  setLegendOpen(false);

  const parcel = await fetchParcelAtPoint(latlng.lat, latlng.lng);
  if (requestId !== latestMapIdentifyRequestId) return;

  if (!parcel) {
    showToast('No Rutherford County parcel found at that location.');
    return;
  }

  await selectIdentifiedParcel(parcel);
}

function applyPriceBucketFilter(start: number, end: number): void {
  minPrice.value = formatCurrencyInputValue(start);
  maxPrice.value = formatCurrencyInputValue(end);
  applyCurrentFilters();
}

function scheduleLiveFilterApply(): void {
  if (liveFilterTimer !== null) window.clearTimeout(liveFilterTimer);
  liveFilterTimer = window.setTimeout(() => {
    liveFilterTimer = null;
    applyCurrentFilters();
  }, LIVE_FILTER_DELAY_MS);
}

function scheduleLiveDataRefresh(): void {
  if (liveRefreshTimer !== null) window.clearTimeout(liveRefreshTimer);
  liveRefreshTimer = window.setTimeout(() => {
    liveRefreshTimer = null;
    refreshData();
  }, LIVE_REFRESH_DELAY_MS);
}

function renderSales(records: SaleRecord[]): void {
  closeParcelPanel(false);
  renderActiveFilters();
  renderDistribution(records, getFilters(), distributionSummary, distributionChart);
  renderSaleMarkers(map, salesLayer, records, selectSale, parcelBoundaryLayer, { animate: true });
  resetNavigationHistory();
  visibleSalesCount.textContent = records.length.toLocaleString();

  if (records.length === 0) {
    statusText.textContent = 'No sales match the selected filters.';
    return;
  }

  statusText.textContent = `${records.length.toLocaleString()} of ${allSales.length.toLocaleString()} sales visible from ${dateFrom.value} through ${dateTo.value}.`;
}

function setMapLoading(message: string | null): void {
  if (message === null) {
    const elapsed = mapLoadingStartedAt > 0 ? window.performance.now() - mapLoadingStartedAt : Infinity;
    const minimumVisibleMs = 550;

    if (mapLoading.hidden || elapsed >= minimumVisibleMs) {
      if (mapLoadingHideTimer !== null) {
        window.clearTimeout(mapLoadingHideTimer);
        mapLoadingHideTimer = null;
      }
      mapLoading.hidden = true;
      mapShell.setAttribute('aria-busy', 'false');
      mapLoadingStartedAt = 0;
      return;
    }

    if (mapLoadingHideTimer !== null) {
      window.clearTimeout(mapLoadingHideTimer);
    }

    mapLoadingHideTimer = window.setTimeout(() => {
      mapLoadingHideTimer = null;
      mapLoading.hidden = true;
      mapShell.setAttribute('aria-busy', 'false');
      mapLoadingStartedAt = 0;
    }, minimumVisibleMs - elapsed);
    return;
  }

  if (mapLoadingHideTimer !== null) {
    window.clearTimeout(mapLoadingHideTimer);
    mapLoadingHideTimer = null;
  }

  if (mapLoading.hidden) {
    mapLoadingStartedAt = window.performance.now();
  }

  mapLoadingText.textContent = message;
  mapLoading.hidden = false;
  mapShell.setAttribute('aria-busy', 'true');
}

async function loadCountyBoundary(): Promise<void> {
  const boundary = await fetchCountyBoundary();
  if (boundary) renderCountyBoundary(countyBoundaryLayer, boundary);
}

function scheduleRenderSales(records: SaleRecord[], loadingMessage: string | null, hideLoadingAfterRender = true): void {
  const requestId = ++latestSalesRenderRequestId;

  if (pendingSalesRenderFrame !== null) {
    window.cancelAnimationFrame(pendingSalesRenderFrame);
    pendingSalesRenderFrame = null;
  }

  if (loadingMessage !== null) {
    setMapLoading(loadingMessage);
  }

  // Coalesce rapid filter input into the next animation frame so Leaflet layer rebuilds and
  // histogram DOM work happen once for the latest state.
  pendingSalesRenderFrame = window.requestAnimationFrame(() => {
    if (requestId !== latestSalesRenderRequestId) return;
    pendingSalesRenderFrame = null;
    renderSales(records);

    if (!hideLoadingAfterRender) return;
    setMapLoading(null);
  });
}

function showToast(message: string): void {
  toast.textContent = message;
  toast.hidden = false;
  if (toastTimer !== null) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toast.hidden = true;
    toastTimer = null;
  }, 6000);
}

async function refreshData(): Promise<void> {
  const requestId = ++latestRefreshRequestId;
  const filters = getFilters();

  if (new URLSearchParams(window.location.search).has('demo')) {
    // Demo mode uses local sample sales so UI work can be verified without ArcGIS availability or network access.
    allSales = demoSales();
    visibleSalesCount.textContent = allSales.length.toLocaleString();
    renderDynamicFilters(allSales);
    scheduleRenderSales(getFilteredSales(), null);
    statusText.textContent = `${getFilteredSales().length.toLocaleString()} of ${allSales.length.toLocaleString()} sales visible. Demo preview mode: sample markers are shown so the static layout can be reviewed before live GIS data is reachable.`;
    showToast('Demo preview mode is active. Remove ?demo=1 to query GisCharter cached sales.');
    return;
  }

  const cached = loadCache(filters);
  let loadingMessage = 'Loading sales from GisCharter cached sales…';

  if (cached) {
    // Keep the last cached result visible while the browser fetches fresh data, so the map does not go blank on repeat visits.
    allSales = cached.records;
    renderDynamicFilters(allSales);
    scheduleRenderSales(getFilteredSales(), 'Refreshing sales from GisCharter cached sales…', false);
    showToast(`Showing cached sales from ${new Date(cached.savedAt).toLocaleString()} while refreshing.`);
    loadingMessage = 'Refreshing sales from GisCharter cached sales…';
  }

  try {
    setMapLoading(loadingMessage);
    visibleSalesCount.textContent = '…';
    statusText.textContent = loadingMessage;
    const records = await fetchSales(filters, (count) => {
      if (requestId !== latestRefreshRequestId) return;
      statusText.textContent = `Loaded ${count.toLocaleString()} sales…`;
    });
    if (requestId !== latestRefreshRequestId) return;
    allSales = records;
    renderDynamicFilters(allSales);
    saveCache(filters, records);
    scheduleRenderSales(getFilteredSales(), null);
  } catch (error) {
    if (requestId !== latestRefreshRequestId) return;
    const message = error instanceof Error ? error.message : 'Unknown network error';
    statusText.textContent = cached
      ? `Network refresh failed; still showing cached data. ${message}`
      : `Could not load GisCharter cached sales. ${message}`;
    showToast(statusText.textContent);
    setMapLoading(null);
  }
}

// Incrementing request ids keeps stale fetches from replacing newer filter results when users change the sale window quickly.
let latestRefreshRequestId = 0;

function applyCurrentFilters(): void {
  // All current filters are applied client-side after data loads; only the sale window needs a new ArcGIS query.
  scheduleRenderSales(getFilteredSales(), 'Updating map markers…');
}

presetMonths.addEventListener('change', () => {
  if (presetMonths.value === 'custom') {
    syncCustomDateControls();
    renderActiveFilters();
    return;
  } else {
    setDefaultDates(Number(presetMonths.value));
  }
  renderActiveFilters();
  closeDropdowns();
  // Preset changes alter the query window, so refresh immediately instead of waiting for the debounced client-side path.
  void refreshData();
});

for (const option of presetOptions) {
  option.addEventListener('click', () => {
    const value = option.dataset.presetMonths;
    if (!value) return;
    presetMonths.value = value;
    presetMonths.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

for (const input of [dateFrom, dateTo]) {
  input.addEventListener('input', () => {
    presetMonths.value = 'custom';
    syncCustomDateControls();
    renderActiveFilters();
    // Custom date edits also change the query window, so debounce a fresh fetch instead of only rerendering the current records.
    scheduleLiveDataRefresh();
  });
}

filtersForm.addEventListener('submit', (event) => {
  event.preventDefault();
  refreshData();
});

for (const input of [minPrice, maxPrice, acreageMin, acreageMax]) {
  input.addEventListener('input', scheduleLiveFilterApply);
}

for (const input of [minPrice, maxPrice]) {
  input.addEventListener('input', () => renderPriceToggleLabel());
  input.addEventListener('blur', () => formatCurrencyInput(input));
  input.addEventListener('focus', () => {
    const parsed = parseCurrencyInput(input.value);
    if (parsed !== null) input.value = String(parsed);
  });
}

function handleSqftInput(changedInput: HTMLInputElement): void {
  if (!sqftBounds) return;

  const minValue = Number(sqftMin.value);
  const maxValue = Number(sqftMax.value);

  if (changedInput === sqftMin && minValue > maxValue) {
    sqftMax.value = String(minValue);
  }

  if (changedInput === sqftMax && maxValue < minValue) {
    sqftMin.value = String(maxValue);
  }

  selectedSqftMin = Number(sqftMin.value);
  selectedSqftMax = Number(sqftMax.value);
  updateSqftLabel();
  updateSqftRangeTrack();
  applyCurrentFilters();
}

function beginSqftRangeDrag(event: PointerEvent): void {
  if (!sqftBounds || sqftMin.disabled || sqftMax.disabled || event.button !== 0) return;

  const value = valueFromSqftPointer(event.clientX);
  if (value === null) return;

  event.preventDefault();
  const input = closestSqftInput(value);
  sqftDragState = {
    pointerId: event.pointerId,
    input,
  };

  setActiveSqftInput(input);
  sqftRangeControl.setPointerCapture(event.pointerId);
  input.focus();
  setSqftInputValue(input, value);
}

function updateSqftRangeDrag(event: PointerEvent): void {
  if (!sqftDragState || event.pointerId !== sqftDragState.pointerId) return;

  const value = valueFromSqftPointer(event.clientX);
  if (value === null) return;

  event.preventDefault();
  setSqftInputValue(sqftDragState.input, value);
}

function endSqftRangeDrag(event: PointerEvent): void {
  if (!sqftDragState || event.pointerId !== sqftDragState.pointerId) return;

  sqftDragState = null;
  setActiveSqftInput(null);

  if (sqftRangeControl.hasPointerCapture(event.pointerId)) {
    sqftRangeControl.releasePointerCapture(event.pointerId);
  }
}

sqftRangeControl.addEventListener('pointerdown', beginSqftRangeDrag, { capture: true });
sqftRangeControl.addEventListener('pointermove', updateSqftRangeDrag);
sqftRangeControl.addEventListener('pointerup', endSqftRangeDrag);
sqftRangeControl.addEventListener('pointercancel', endSqftRangeDrag);
sqftMin.addEventListener('input', () => handleSqftInput(sqftMin));
sqftMax.addEventListener('input', () => handleSqftInput(sqftMax));

resetFilters.addEventListener('click', resetAllFilters);

function handleDynamicOptionChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  if (!['saleType', 'landClass'].includes(input.name)) return;

  syncSelectionFromInputs();
  applyCurrentFilters();
}

saleTypeOptionsContainer.addEventListener('change', handleDynamicOptionChange);
landClassOptionsContainer.addEventListener('change', handleDynamicOptionChange);

window.addEventListener('popstate', (event) => {
  if (!isGisCharterHistoryState(event.state)) return;

  const nextIndex = event.state.navigationIndex;
  const nextEntry = navigationEntries[nextIndex];
  if (!nextEntry) {
    activeNavigationIndex = 0;
    resetNavigationHistory();
    return;
  }

  activeNavigationIndex = nextIndex;
  void restoreNavigationEntry(nextEntry);
});

for (const { toggle, panel } of dropdowns) {
  toggle.addEventListener('click', () => {
    toggleDropdown(toggle, panel);
    setLegendOpen(false);
  });
}

document.addEventListener('pointerdown', (event) => {
  const target = event.target as Node;
  if (!dropdowns.some(({ toggle, panel }) => toggle.contains(target) || panel.contains(target))) {
    closeDropdowns();
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  closeDropdowns();
  setLegendOpen(false);
  if (!parcelPanel.hidden) closeParcelPanel();
});

legendToggle.addEventListener('click', () => {
  const shouldOpen = legendPanel.hidden === true;
  closeDropdowns();
  setLegendOpen(shouldOpen);
});

legendClose.addEventListener('click', () => {
  setLegendOpen(false);
  legendToggle.focus();
});

parcelPanelClose.addEventListener('click', () => {
  closeParcelPanel();
});

parcelBack.addEventListener('click', restorePreviousMapView);

map.on('click', (event) => {
  void identifyParcelAtPoint(event.latlng);
});

distributionChart.addEventListener('click', (event) => {
  const bar = (event.target as HTMLElement).closest<HTMLButtonElement>('.distribution-bar-item');
  if (!bar) return;

  const start = Number(bar.dataset.priceStart);
  const end = Number(bar.dataset.priceEnd);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return;

  applyPriceBucketFilter(start, end);
});

setDefaultDates(12);
initializeMapInstruction();
renderActiveFilters();
renderDistribution([], getFilters(), distributionSummary, distributionChart);
registerTestHooks();
void loadCountyBoundary();
refreshData();
